import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { TextOneLine } from '@actual-app/components/text-one-line';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type { DatosPrestamo } from '@actual-app/core/shared/prestamos';
import type { AccountEntity } from '@actual-app/core/types/models';
import { css } from '@emotion/css';

import { useDeuda } from '#components/mobile/deudas/useDeuda';
import { Icono } from '#components/mobile/ui/Icono';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import { IconoCaja } from '#components/mobile/ui/IconoCaja';
import {
  color,
  colorCategoria,
  num,
  TACTIL,
} from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useSheetValue } from '#hooks/useSheetValue';
import { useSelector } from '#redux';
import {
  accountBalance,
  accountBalanceUncleared,
  hastaHoy,
} from '#spreadsheet/bindings';

import { estadoBanco } from './banco';
import { faltanDatosPrestamo } from './cuentas';
import type { TipoCuenta } from './cuentas';

/** Alto mínimo de la fila de una cuenta. */
const ALTO_FILA = 60;

export const COLOR_TIPO: Record<TipoCuenta, string> = {
  corriente: colorCategoria(0),
  ahorro: color.ok,
  inversion: colorCategoria(4),
  prestamo: color.bad,
};

export function iconoDeCuenta(
  tipo: TipoCuenta,
  nombre: string,
  datos?: DatosPrestamo | null,
): NombreIcono {
  switch (tipo) {
    case 'ahorro':
      return 'piggy';
    case 'inversion':
      return 'up';
    case 'prestamo':
      return datos?.tipo === 'autoLoan' ||
        /coche|cotxe|car\b|auto/i.test(nombre)
        ? 'car'
        : datos?.tipo === 'mortgage' ||
            /hipoteca|mortgage|casa|piso/i.test(nombre)
          ? 'home'
          : 'coins';
    default:
      return /com[uú]n|comuna|conjunta|joint/i.test(nombre) ? 'users' : 'bank';
  }
}

export function useNombreTipo() {
  const { t } = useTranslation();
  return (tipo: TipoCuenta) =>
    ({
      corriente: t('Checking'),
      ahorro: t('Savings'),
      inversion: t('Investment'),
      prestamo: t('Loan'),
    })[tipo];
}

type FilaProps = {
  cuenta: AccountEntity;
  tipo: TipoCuenta;
  /** Saldos a fin de mes (solo ahorro e inversión llevan minigráfica). */
  serie?: readonly number[];
  datos?: DatosPrestamo | null;
  primera: boolean;
  /** Modo reordenar: asa en vez del menú, y sin navegar. */
  reordenando?: boolean;
  onAbrir: () => void;
  onMenu: () => void;
};

/** Fila de una cuenta (concepto B «Carta náutica»). */
export function FilaCuenta(props: FilaProps) {
  return props.tipo === 'prestamo' ? (
    <FilaPrestamo {...props} />
  ) : (
    <FilaNormal {...props} />
  );
}

function FilaNormal({
  cuenta,
  tipo,
  serie,
  primera,
  reordenando,
  onAbrir,
  onMenu,
}: FilaProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const nombreTipo = useNombreTipo();
  const pendiente =
    useSheetValue<'account', 'balanceUncleared'>(
      hastaHoy(accountBalanceUncleared(cuenta.id)),
    ) ?? 0;
  const saldo =
    useSheetValue<'account', 'balance'>(hastaHoy(accountBalance(cuenta.id))) ??
    0;

  const syncingIds = useSelector(st => st.account.accountsSyncing);
  const estado = syncingIds.includes(cuenta.id)
    ? ({ tipo: 'sincronizando' } as const)
    : estadoBanco(cuenta);
  const hace = cuenta.last_sync
    ? monthUtils.formatDistance(
        new Date(Number(cuenta.last_sync)),
        new Date(),
        locale,
        { addSuffix: true },
      )
    : null;
  const banco = cuenta.bankName || t('Bank');
  // Con banco: «CaixaBank · hace 2 h» o el problema, dicho en claro.
  const [detalle, detalleTono] =
    estado.tipo === 'sin-banco'
      ? [nombreTipo(tipo), undefined]
      : estado.tipo === 'sincronizando'
        ? [t('{{bank}} · syncing…', { bank: banco }), undefined]
        : estado.tipo === 'reconectar'
          ? [t('Permission expired · reconnect'), color.bad]
          : estado.tipo === 'mas-tarde'
            ? [t('The bank is not answering · try later'), color.warn]
            : estado.tipo === 'cuenta-perdida'
              ? [t('The bank no longer finds this account'), color.bad]
              : estado.tipo === 'error'
                ? [t('Sync error · tap ⋯'), color.bad]
                : [hace ? `${banco} · ${hace}` : banco, undefined];

  return (
    <BaseFila
      cuenta={cuenta}
      icono={iconoDeCuenta(tipo, cuenta.name)}
      tono={COLOR_TIPO[tipo]}
      sincronizada={!!cuenta.bank}
      detalle={detalle}
      detalleTono={detalleTono}
      grafica={
        (tipo === 'ahorro' || tipo === 'inversion') &&
        serie &&
        serie.length > 1 ? (
          <Minigrafica valores={serie} tono={COLOR_TIPO[tipo]} />
        ) : null
      }
      saldo={saldo}
      nota={
        pendiente !== 0 ? (
          <span style={{ color: color.warn }}>
            {t('{{amount}} pend.', { amount: format(pendiente, 'financial') })}
          </span>
        ) : null
      }
      primera={primera}
      reordenando={reordenando}
      onAbrir={onAbrir}
      onMenu={onMenu}
    />
  );
}

function FilaPrestamo({
  cuenta,
  datos,
  primera,
  reordenando,
  onAbrir,
  onMenu,
}: FilaProps) {
  const { t, i18n } = useTranslation();
  const format = useFormat();
  const deuda = useDeuda(cuenta, datos ?? null);
  const saldo =
    useSheetValue<'account', 'balance'>(hastaHoy(accountBalance(cuenta.id))) ??
    0;
  const partes: string[] = [];
  if (deuda.interesAnual != null) {
    partes.push(
      t('{{rate}} %', {
        rate: deuda.interesAnual.toLocaleString(i18n.language, {
          maximumFractionDigits: 2,
        }),
      }),
    );
  }
  if (deuda.cuota != null) {
    partes.push(
      t('payment {{amount}}', { amount: format(deuda.cuota, 'financial') }),
    );
  }
  const fin = deuda.amortizacion?.fechaFin;
  if (fin) {
    partes.push(t('free in {{year}}', { year: fin.slice(0, 4) }));
  }
  return (
    <BaseFila
      cuenta={cuenta}
      icono={iconoDeCuenta('prestamo', cuenta.name, datos)}
      tono={COLOR_TIPO.prestamo}
      detalle={partes.length > 0 ? partes.join(' · ') : t('Loan')}
      saldo={saldo}
      nota={
        // B7 · H56: sin interés o cuota, el % pagado no dice nada.
        faltanDatosPrestamo(datos) ? (
          <span style={{ color: color.warn }} data-testid="prestamo-sin-datos">
            <Trans>Missing details</Trans>
          </span>
        ) : (
          t('{{percent}} % paid', {
            percent: Math.round(deuda.porcentajePagado * 100),
          })
        )
      }
      primera={primera}
      reordenando={reordenando}
      onAbrir={onAbrir}
      onMenu={onMenu}
    />
  );
}

function BaseFila({
  cuenta,
  icono,
  tono,
  sincronizada,
  detalle,
  detalleTono,
  grafica,
  saldo,
  nota,
  primera,
  reordenando,
  onAbrir,
  onMenu,
}: {
  cuenta: AccountEntity;
  icono: NombreIcono;
  tono: string;
  sincronizada?: boolean;
  detalle: string;
  detalleTono?: string;
  grafica?: ReactNode;
  saldo: number;
  nota: ReactNode;
  primera: boolean;
  reordenando?: boolean;
  onAbrir: () => void;
  onMenu: () => void;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  return (
    <View
      data-testid="cuenta-fila"
      data-account-id={cuenta.id}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: ALTO_FILA,
        boxShadow: primera ? undefined : `inset 0 1px 0 ${color.line}`,
      }}
    >
      <Button
        variant="bare"
        aria-label={t('View {{name}} transactions', { name: cuenta.name })}
        data-testid="account-list-item"
        isDisabled={reordenando}
        onPress={onAbrir}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 10,
          padding: '8px 0 8px 14px',
          borderRadius: 0,
          color: color.fg,
          textAlign: 'left',
          opacity: 1,
        }}
      >
        <IconoCaja icono={icono} colorPropio={tono} size={32} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <TextOneLine
            style={{ fontSize: 15, fontWeight: 500 }}
            data-testid="account-name"
          >
            {cuenta.name}
          </TextOneLine>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
              marginTop: 2,
              minWidth: 0,
              color: detalleTono ?? color.fg3,
            }}
          >
            {sincronizada && <Icono nombre="refresh" size={11} />}
            <TextOneLine
              style={{ ...num, fontSize: 12, fontWeight: 600 }}
              data-testid="cuenta-detalle"
            >
              {detalle}
            </TextOneLine>
          </View>
        </View>
        {grafica}
        <View style={{ alignItems: 'flex-end', flexShrink: 0 }}>
          <PrivacyFilter>
            <Text
              style={{
                ...num,
                fontSize: 14,
                fontWeight: 700,
                color: saldo < 0 ? color.bad : color.fg,
                whiteSpace: 'nowrap',
              }}
              data-testid="account-balance"
            >
              {format(saldo, 'financial')}
            </Text>
          </PrivacyFilter>
          <Text
            style={{
              ...num,
              fontSize: 11.5,
              fontWeight: 600,
              color: color.fg3,
              marginTop: 2,
              whiteSpace: 'nowrap',
            }}
          >
            <PrivacyFilter>{nota}</PrivacyFilter>
          </Text>
        </View>
      </Button>
      {reordenando ? (
        <View
          aria-hidden
          style={{
            width: 40,
            height: TACTIL,
            alignItems: 'center',
            justifyContent: 'center',
            color: color.fg3,
            flexShrink: 0,
          }}
        >
          <Icono nombre="grip" size={18} />
        </View>
      ) : (
        <Button
          variant="bare"
          aria-label={t('{{name}} options', { name: cuenta.name })}
          data-testid="cuenta-menu"
          onPress={onMenu}
          className={ZONA_MENU}
          style={{
            overflow: 'visible',
            width: 40,
            height: TACTIL,
            flexShrink: 0,
            borderRadius: 12,
            color: color.fg3,
            padding: 0,
          }}
        >
          <Icono nombre="more" size={18} />
        </Button>
      )}
    </View>
  );
}

/**
 * Zona táctil del «⋯» (B2 · H04): el botón mide 40 × 44; la zona llega a
 * 44 × 60, toda la altura de la fila (`minHeight: 60`), sin pisar las filas
 * vecinas y solo 2 px sobre el botón de la fila (el saldo, a la izquierda).
 */
export const ZONA_MENU_FILA = { ancho: 44, alto: ALTO_FILA };

const ZONA_MENU = css({
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: ZONA_MENU_FILA.ancho,
    height: ZONA_MENU_FILA.alto,
    transform: 'translate(-50%, -50%)',
    background: 'transparent',
  },
});

/** Minigráfica del saldo a fin de mes (ahorro e inversión). */
function Minigrafica({
  valores,
  tono,
}: {
  valores: readonly number[];
  tono: string;
}) {
  const w = 52;
  const h = 24;
  const min = Math.min(...valores);
  const max = Math.max(...valores);
  const span = max - min || 1;
  const d = valores
    .map(
      (v, i) =>
        `${i === 0 ? 'M' : 'L'}${((i * (w - 4)) / (valores.length - 1) + 2).toFixed(1)},${(
          h -
          2 -
          ((v - min) * (h - 4)) / span
        ).toFixed(1)}`,
    )
    .join(' ');
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden
      data-testid="minigrafica"
      style={{ flexShrink: 0 }}
    >
      <path
        d={d}
        fill="none"
        stroke={tono}
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
